import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import { AnimatePresence, motion } from "framer-motion";
import { agregarCita } from "../../slices/calendarSlice";
import bootstrap5Plugin from '@fullcalendar/bootstrap5';




export default function ModalCitasCliente({
  isModalOpen,
  setIsModalOpen,
  success,
  errorDate,
}) {
  const dispatch = useDispatch();

  const disabledDates = ["2025-02-20", "2025-03-05"];

  const [selectedTime, setSelectedTime] = useState(null);
  const [citasAgendadas, setCitasAgendadas] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [disabledTimes, setDisabledTimes] = useState([]);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [incrementMinutes, setIncrementMinutes] = useState(15);

  // Función auxiliar para convertir hora a minutos
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + (minutes || 0);
  };

  // Función para verificar si hay suficiente espacio entre citas
  const hasEnoughSpace = (time) => {
    const timeInMinutes = timeToMinutes(time);

    // Convertir las citas agendadas del mismo día a minutos
    const citasDelDia = citasAgendadas
      .filter((cita) => cita.fecha === selectedDate)
      .map((cita) => timeToMinutes(cita.hora))
      .sort((a, b) => a - b);

    // Verificar si hay suficiente espacio antes de la siguiente cita
    const nextAppointment = citasDelDia.find((citaTime) => citaTime > timeInMinutes);
    if (nextAppointment && nextAppointment - timeInMinutes < 75) {
      // 75 minutos = 1 hora y 15 minutos
      return false;
    }

    // Verificar si hay suficiente espacio después de la cita anterior
    const previousAppointment = citasDelDia
      .reverse()
      .find((citaTime) => citaTime < timeInMinutes);
    if (previousAppointment && timeInMinutes - previousAppointment < 75) {
      return false;
    }

    return true;
  };

  const generateTimes = () => {
    const allTimes = [];
    const startHour = 9;
    const endHour = 21;

    for (let hour = startHour; hour <= endHour; hour++) {
      // Generar horarios base y con incrementos
      const basetime = `${hour}:00`;
      const incrementTime = `${hour}:${incrementMinutes}`;

      // Solo agregar horarios si hay suficiente espacio
      if (hasEnoughSpace(basetime)) {
        allTimes.push(basetime);
      }
      if (hour < endHour && hasEnoughSpace(incrementTime)) {
        allTimes.push(incrementTime);
      }
    }

    return allTimes.filter((time) => {
      return (
        !citasAgendadas.some(
          (cita) => cita.fecha === selectedDate && cita.hora === time
        ) &&
        !disabledTimes.includes(time)
      );
    });
  };

  const adjustNextHour = (time) => {
    const [hour, minutes] = time.split(":").map(Number);

    // Si el tiempo actual no tiene minutos (XX:00)
    if (minutes === 0) {
      return `${hour}:15`;
    }
    // Si el tiempo actual tiene minutos (XX:15 o XX:30)
    else {
      const nextHour = hour + 1;
      const nextMinutes = minutes + 15;
      if (nextMinutes >= 60) {
        return `${nextHour}:00`;
      }
      return `${hour}:${nextMinutes}`;
    }
  };

  const handleTimeSelection = (time) => {
    setSelectedTime(time);

    const [selectedHour, selectedMinutes] = time.split(":").map(Number);

    // Calcular tiempos anterior y siguiente basados en el incremento
    const previousTime =
      selectedMinutes === 0
        ? `${selectedHour - 1}:45`
        : `${selectedHour}:${selectedMinutes - 15}`;

    const nextTime = adjustNextHour(time);

    setDisabledTimes([previousTime, nextTime]);
    setIncrementMinutes((prev) => (prev === 15 ? 30 : 15));
  };

  useEffect(() => {
    if (isTimeModalOpen) {
      setDisabledTimes([]);
      setIncrementMinutes(15);
    }
  }, [isTimeModalOpen]);

  const times = generateTimes();

  const [eventName, setEventName] = useState("");
  const [events, setEvents] = useState([]);

  const handleDateClick = (arg) => {
    if (disabledDates.includes(arg.dateStr)) {
      alert("Esta fecha no está disponible.");
      return;
    }
    setSelectedDate(arg.dateStr);
    setIsTimeModalOpen(true);
  };

  const handleTimeSelect = () => {
    if (eventName.trim() === "") {
      errorDate();
      return;
    }

    if (!selectedTime) {
      alert("Por favor, selecciona una hora.");
      return;
    }

    const eventData = {
      id: String(new Date().getTime()),
      title: eventName,
      date: selectedDate,
      time: selectedTime,
    };

    setCitasAgendadas((prev) => [
      ...prev,
      { fecha: selectedDate, hora: selectedTime },
    ]);

    setEvents([...events, eventData]);
    setIsTimeModalOpen(false);
    setIsModalOpen(false);
    success();
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          key="modal-background"
          onClick={() => setIsModalOpen(false)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <motion.div
            key="modal-content"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="bg-background rounded-lg shadow-lg p-6 w-[1200px] h-[600px] mx-4 border border-gray-100"
          >
            <div className="font-poppina w-[1150px] bg-background dark:text-blue-500 ">
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin, bootstrap5Plugin]}
                
                initialView="dayGridMonth"
                headerToolbar={{
                  left: "prev,next today",
                  center: "title",
                  right: "dayGridMonth,dayGridWeek,dayGridDay",
                }}
                height="540px"
                dateClick={handleDateClick}
                events={events}
                locale={esLocale}
                dayCellClassNames={(arg) => 
                  disabledDates.includes(arg.date.toISOString().split("T")[0]) 
                    ? "fc-disabled-day" 
                    : ""
                }
              />
            </div>
          </motion.div>
        </motion.div>
      )}

      {isTimeModalOpen && (
        <motion.div
          key="time-modal-background"
          onClick={() => setIsTimeModalOpen(false)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <motion.div
            key="time-modal-content"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="bg-background rounded-lg shadow-lg p-6 w-96 mx-4 border border-gray-100"
          >
            <h2 className="text-lg font-semibold mb-4 dark:text-white ">
              Agregar cita
            </h2>
            <input
              type="text"
              placeholder="Nombre de la cita"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="w-full p-2 border border-gray-300 text-text bg-background rounded mb-4"
            />
            <div className="grid grid-cols-3 gap-2 mb-4">
              {times.map((time, index) => {
                const isDisabled = disabledTimes.includes(time);

                return (
                  <button
                    className={`bg-blue-500 text-white p-2 rounded hover:bg-blue-600 ${
                      isDisabled ? "opacity-50 cursor-not-allowed" : ""
                    } ${selectedTime === time ? "bg-blue-700" : ""}`}
                    key={index}
                    onClick={() => !isDisabled && handleTimeSelection(time)}
                    disabled={isDisabled}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
            <button
              onClick={handleTimeSelect}
              className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600"
            >
              Guardar cita
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}